import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, XCircle, Clock, Download, CheckCircle } from "lucide-react";
import { api } from "../lib/api";

export default function DigitalOrderPending() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session");
  const [state, setState] = useState("polling"); // polling | paid | failed | timeout
  const [downloads, setDownloads] = useState([]); // [{ product_name, download_url, file_name }]

  useEffect(() => {
    document.title = "Confirming payment – Cheap4Teach";
    if (!sessionId) { nav("/"); return; }

    let attempts = 0;
    const MAX = 18; // 36 seconds total

    const poll = setInterval(async () => {
      attempts++;
      try {
        const r = await api.get(`/wipay/digital-session/${sessionId}`);
        const { status, downloads: dl } = r.data;
        if (status === "paid") {
          clearInterval(poll);
          setDownloads(dl || []);
          setState("paid");
        } else if (status === "failed") {
          clearInterval(poll);
          setState("failed");
        } else if (attempts >= MAX) {
          clearInterval(poll);
          setState("timeout");
        }
      } catch {
        if (attempts >= MAX) {
          clearInterval(poll);
          setState("timeout");
        }
      }
    }, 2000);

    return () => clearInterval(poll);
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state === "paid") {
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center">
        <div className="c4t-card">
          <CheckCircle className="w-16 h-16 text-[#06D6A0] mx-auto mb-4" strokeWidth={2} />
          <h1 className="text-3xl font-['Fredoka'] font-bold">Payment Confirmed!</h1>
          <p className="c4t-desc mt-2 mb-6">Your purchase was successful. Download your files below. Links expire in 48 hours.</p>

          <div className="space-y-3 mb-6">
            {downloads.map((d, i) => (
              <a
                key={i}
                href={d.download_url}
                download={d.file_name}
                target="_blank"
                rel="noreferrer"
                className="c4t-btn c4t-btn-pink w-full !no-underline flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" /> {d.product_name || d.file_name}
              </a>
            ))}
          </div>

          <p className="text-xs text-[#455A64]">
            A confirmation email with download links has been sent to you. You can also re-download anytime from{" "}
            <Link to="/account" className="text-[#118AB2] hover:text-[#FF477E]">My Orders</Link>.
          </p>
          <Link to="/digital-shop" className="block mt-5 text-sm text-[#455A64] hover:text-[#FF477E]">
            Back to Digital Shop
          </Link>
        </div>
      </div>
    );
  }

  if (state === "failed") {
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center">
        <div className="c4t-card">
          <XCircle className="w-16 h-16 text-[#FF477E] mx-auto mb-4" strokeWidth={2} />
          <h1 className="text-3xl font-['Fredoka'] font-bold">Payment declined</h1>
          <p className="c4t-desc mt-2">Your card was not charged. Please try again or use a different card.</p>
          <Link to="/digital-shop" className="mt-6 inline-flex c4t-btn c4t-btn-pink">Try again</Link>
        </div>
      </div>
    );
  }

  if (state === "timeout") {
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center">
        <div className="c4t-card">
          <Clock className="w-16 h-16 text-[#FFC500] mx-auto mb-4" strokeWidth={2} />
          <h1 className="text-3xl font-['Fredoka'] font-bold">Still processing…</h1>
          <p className="c4t-desc mt-2">If your payment went through, a confirmation email with download links is on its way. If you don't receive one within a few minutes, please contact us.</p>
          <Link to="/" className="mt-6 inline-flex c4t-btn c4t-btn-pink">Go home</Link>
          <Link to="/account" className="block mt-3 text-sm text-[#118AB2] hover:text-[#FF477E]">Check My Orders</Link>
        </div>
      </div>
    );
  }

  // Polling spinner
  return (
    <div className="max-w-md mx-auto px-6 py-20 text-center">
      <div className="c4t-card">
        <Loader2 className="w-16 h-16 text-[#06D6A0] mx-auto mb-4 animate-spin" strokeWidth={2} />
        <h1 className="text-3xl font-['Fredoka'] font-bold">Confirming your payment…</h1>
        <p className="c4t-desc mt-2">Please don't close this page. We're waiting for confirmation from WiPay.</p>
      </div>
    </div>
  );
}
